import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { formatWeightKg, parseWeightKg, stepWeightKg } from '@core/weight-quantity';
import { BodyPortalDirective } from '@shared/directives/body-portal.directive';
import { barcodeVideoConstraints } from '@shared/services/barcode-camera';

@Component({
  selector: 'app-kg-quantity',
  standalone: true,
  imports: [CommonModule, FormsModule, BodyPortalDirective],
  templateUrl: './kg-quantity.component.html',
  styleUrl: './kg-quantity.component.css'
})
export class KgQuantityComponent implements OnChanges {
  @Input() quantity = 1;
  @Input() soldByWeight = false;
  @Input() disabled = false;
  @Input() estimating = false;
  @Input() label = '';
  @Input() testId = 'quantity';

  @Output() quantityChange = new EventEmitter<number>();
  @Output() photo = new EventEmitter<string>();

  @ViewChild('cameraVideo') private cameraVideo?: ElementRef<HTMLVideoElement>;

  draft = '1';
  editing = false;
  cameraOpen = false;
  cameraError: string | null = null;
  private cameraStream: MediaStream | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['quantity'] || changes['soldByWeight']) && !this.editing) {
      this.draft = this.soldByWeight ? formatWeightKg(this.quantity) : String(this.quantity);
    }
  }

  get controlsLocked(): boolean {
    return this.disabled || this.estimating;
  }

  get canDecrease(): boolean {
    if (this.controlsLocked) return false;
    if (!this.soldByWeight) return this.quantity > 1;
    return stepWeightKg(this.quantity, -1) != null;
  }

  get canIncrease(): boolean {
    if (this.controlsLocked) return false;
    if (!this.soldByWeight) return true;
    return stepWeightKg(this.quantity, 1) != null;
  }

  shownQuantity(): string {
    return this.soldByWeight ? formatWeightKg(this.quantity) : this.formatUnit(this.quantity);
  }

  bump(direction: -1 | 1): void {
    if (this.controlsLocked) return;
    if (this.soldByWeight) {
      const next = stepWeightKg(this.quantity, direction);
      if (next == null) return;
      this.quantityChange.emit(next);
      return;
    }
    const next = this.quantity + direction;
    if (next < 1) return;
    this.quantityChange.emit(next);
  }

  onDraftFocus(): void {
    this.editing = true;
  }

  commitDraft(): void {
    this.editing = false;
    const parsed = parseWeightKg(this.draft);
    this.draft = formatWeightKg(this.quantity);
    if (parsed == null || parsed === this.quantity) return;
    this.quantityChange.emit(parsed);
  }

  async openCamera(): Promise<void> {
    if (this.controlsLocked || !this.soldByWeight) return;
    this.cameraError = null;
    this.cameraOpen = true;
    try {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      const video = this.cameraVideo?.nativeElement;
      if (!video) throw new Error('missing-video');
      const stream = await navigator.mediaDevices.getUserMedia(barcodeVideoConstraints());
      this.cameraStream = stream;
      video.srcObject = stream;
      video.muted = true;
      await video.play();
    } catch {
      this.closeCamera();
      this.cameraError = 'לא ניתן לפתוח את המצלמה.';
      this.cameraOpen = true;
    }
  }

  capture(): void {
    const video = this.cameraVideo?.nativeElement;
    const frame = video ? this.captureFrame(video) : null;
    this.closeCamera();
    if (!frame) {
      this.cameraError = 'לא הצלחנו לצלם. נסו שוב.';
      this.cameraOpen = true;
      return;
    }
    this.photo.emit(frame);
  }

  closeCamera(): void {
    this.cameraOpen = false;
    this.stopStream();
  }

  private formatUnit(quantity: number): string {
    return Number.isInteger(quantity)
      ? String(quantity)
      : quantity.toLocaleString('he-IL', { maximumFractionDigits: 2 });
  }

  private captureFrame(video: HTMLVideoElement): string | null {
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0, width, height);
      return canvas.toDataURL('image/jpeg', 0.82);
    } catch {
      return null;
    }
  }

  private stopStream(): void {
    const stream = this.cameraStream;
    this.cameraStream = null;
    if (!stream) return;
    for (const track of stream.getTracks()) {
      try {
        track.stop();
      } catch {
        // The camera is already closed.
      }
    }
    const video = this.cameraVideo?.nativeElement;
    if (video) video.srcObject = null;
  }
}
